import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Receipt, Download, Crown, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { getPaymentEnvironment } from "@/lib/stripe";
import { listReceipts, getInvoiceUrl, type Receipt as ReceiptRow } from "@/utils/billing.functions";
import { toast } from "sonner";
import { trackBillingFunnel } from "@/lib/billing-funnel";

export const Route = createFileRoute("/billing")({
  component: BillingPage,
  head: () => ({
    meta: [
      { title: "Receipts & Pro Timeline — Inquira" },
      {
        name: "description",
        content: "View your Inquira Pro payment receipts, download invoices, and see your full Pro access timeline.",
      },
      { property: "og:title", content: "Receipts & Pro Timeline — Inquira" },
      { property: "og:description", content: "Past purchases, downloadable invoices, and a timeline of your Pro plan changes." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Receipts & Pro Timeline — Inquira" },
      { name: "twitter:description", content: "Past purchases, invoices, and your Pro access history." },
    ],
  }),
});

interface TimelineRow {
  id: string;
  event_type: string;
  price_id: string | null;
  status: string | null;
  detail: string | null;
  effective_at: string;
  amount_cents: number | null;
  currency: string | null;
}

const PLAN_LABEL: Record<string, string> = {
  inquira_pro_monthly: "Pro Monthly",
  inquira_pro_yearly: "Pro Yearly",
};

const EVENT_LABEL: Record<string, string> = {
  started: "Pro started",
  plan_changed: "Plan changed",
  cancellation_scheduled: "Cancellation scheduled",
  canceled: "Subscription canceled",
  payment_failed: "Payment failed",
  refunded: "Refunded — access ended",
  chargeback: "Chargeback — access ended",
};

function money(amountCents: number | null, currency: string | null) {
  if (amountCents == null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(
      amountCents / 100,
    );
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

function BillingPage() {
  useEffect(() => {
    trackBillingFunnel("billing_view");
  }, []);
  const { user, isLoading } = useAuth();
  const [receipts, setReceipts] = useState<ReceiptRow[]>([]);
  const [receiptsError, setReceiptsError] = useState<string | null>(null);
  const [loadingReceipts, setLoadingReceipts] = useState(true);
  const [timeline, setTimeline] = useState<TimelineRow[]>([]);
  const [invoiceLoading, setInvoiceLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoadingReceipts(true);
      try {
        const result = await listReceipts({ data: { environment: getPaymentEnvironment() } });
        if (cancelled) return;
        setReceipts(result.receipts);
        setReceiptsError(
          result.error === "no_customer"
            ? "No purchases yet — your receipts will appear here after your first payment."
            : result.error
              ? "We couldn't load your receipts right now. Please try again shortly."
              : null,
        );
      } catch {
        if (!cancelled) setReceiptsError("We couldn't load your receipts right now.");
      } finally {
        if (!cancelled) setLoadingReceipts(false);
      }
    })();

    (async () => {
      const { data } = await supabase
        .from("subscription_events")
        .select("id, event_type, price_id, status, detail, effective_at, amount_cents, currency")
        .eq("user_id", user.id)
        .order("effective_at", { ascending: false })
        .limit(50);
      if (!cancelled) setTimeline((data as TimelineRow[]) ?? []);
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const downloadInvoice = async (transactionId: string) => {
    setInvoiceLoading(transactionId);
    try {
      const { url } = await getInvoiceUrl({
        data: { transactionId, environment: getPaymentEnvironment() },
      });
      if (!url) {
        toast.error("That invoice isn't available yet.");
        return;
      }
      window.open(url, "_blank");
    } catch {
      toast.error("Couldn't open that invoice. Please try again.");
    } finally {
      setInvoiceLoading(null);
    }
  };

  if (!isLoading && !user) {
    return (
      <div className="min-h-screen notebook-lines">
        <div className="max-w-lg mx-auto px-4 py-10 space-y-4 text-center">
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
            Sign in to view receipts
          </h1>
          <Link to="/login">
            <Button variant="scan" size="full">Sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen notebook-lines">
      <PaymentTestModeBanner />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors">
          <ArrowLeft className="size-4" /> Back to Inquira
        </Link>

        <header className="space-y-1">
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink flex items-center gap-2">
            <Receipt className="size-6 text-neon-pink" /> Receipts & Pro timeline
          </h1>
          <p className="text-sm text-ink-muted">Your past payments, invoices, and every change to your Pro access.</p>
        </header>

        <section className="bg-card rounded-2xl border border-rule p-5 space-y-3">
          <h2 className="font-[family-name:var(--font-display)] font-bold text-ink">Payment receipts</h2>
          {loadingReceipts ? (
            <p className="text-sm text-ink-muted">Loading your receipts...</p>
          ) : receiptsError ? (
            <p className="text-sm text-ink-muted">{receiptsError}</p>
          ) : receipts.length === 0 ? (
            <p className="text-sm text-ink-muted">No payments yet.</p>
          ) : (
            <ul className="divide-y divide-rule">
              {receipts.map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{r.description}</p>
                    <p className="text-xs text-ink-muted">
                      {new Date(r.billedAt ?? r.createdAt).toLocaleDateString()} ·{" "}
                      <span className="capitalize">{r.status.replace("_", " ")}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-ink">
                      {money(Number(r.total), r.currency) ?? "—"}
                    </span>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => downloadInvoice(r.id)}
                      disabled={invoiceLoading === r.id}
                    >
                      <Download className="size-4" />
                      {invoiceLoading === r.id ? "Opening..." : "Invoice"}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-card rounded-2xl border border-rule p-5 space-y-4">
          <h2 className="font-[family-name:var(--font-display)] font-bold text-ink">Pro access timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-sm text-ink-muted">
              Nothing here yet — your Pro milestones will appear once you subscribe.
            </p>
          ) : (
            <ol className="space-y-4">
              {timeline.map((e) => {
                const danger = e.event_type === "refunded" || e.event_type === "chargeback" || e.event_type === "payment_failed";
                return (
                  <li key={e.id} className="flex gap-3">
                    <div
                      className={`size-8 rounded-full flex items-center justify-center shrink-0 ${
                        danger ? "bg-destructive/10 text-destructive" : "bg-neon-pink/10 text-neon-pink"
                      }`}
                    >
                      {danger ? (
                        <AlertTriangle className="size-4" />
                      ) : e.event_type === "started" ? (
                        <Crown className="size-4" />
                      ) : (
                        <Clock className="size-4" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink">
                        {EVENT_LABEL[e.event_type] ?? e.event_type}
                        {e.price_id ? ` · ${PLAN_LABEL[e.price_id] ?? e.price_id}` : ""}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {new Date(e.effective_at).toLocaleString()}
                        {money(e.amount_cents, e.currency) ? ` · ${money(e.amount_cents, e.currency)}` : ""}
                      </p>
                      {e.detail && <p className="text-xs text-ink-muted mt-0.5">{e.detail}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        <div className="text-center">
          <Link to="/upgrade" className="text-sm text-neon-pink font-semibold underline">
            Manage your subscription
          </Link>
        </div>
      </div>
    </div>
  );
}
